import { makeAutoObservable, runInAction } from "mobx";

import { createTeam } from "@/api";
import { validateTeamTitle, validateTeamPermalink, validateTeamAbout } from "@/models/api/TeamApi";
import { TeamModel } from "@/models/TeamModel";

import type { FormFieldModel } from "@/components/form/FormFieldModel";
import type { TeamFormFields } from "@/models/api/TeamApi";
import type { RootStore } from "@/store/RootStore";

export class CreateTeamDialogStore {
  rootStore: RootStore;

  isOpen = false;

  step: "title" | "permalink" | "avatar" | "invites" = "title";

  team: TeamModel | null = null;

  /** Selected avatar file before team is created; uploaded after createTeam(). */
  pendingAvatarFile: File | null = null;

  /** Object URL for preview; revoke when clearing. */
  pendingAvatarPreviewUrl: string | null = null;

  formFields: FormFieldModel<TeamFormFields>["formFields"] = {
    permalink: {
      value: "",
      focused: false,
      error: null,
      validate: (value) => validateTeamPermalink(value),
    },
    title: {
      value: "",
      focused: false,
      error: null,
      validate: (value) => validateTeamTitle(value),
    },
    // TODO: implement this step to CreateTeamDialog.tsx
    about: {
      value: "",
      focused: false,
      error: null,
      validate: (value) => validateTeamAbout(value),
    },
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this);
  }

  setPendingAvatar(file: File | null) {
    if (this.pendingAvatarPreviewUrl) {
      URL.revokeObjectURL(this.pendingAvatarPreviewUrl);
    }
    this.pendingAvatarFile = file;
    this.pendingAvatarPreviewUrl = file ? URL.createObjectURL(file) : null;
  }

  private clearPendingAvatar() {
    if (this.pendingAvatarPreviewUrl) {
      URL.revokeObjectURL(this.pendingAvatarPreviewUrl);
    }
    this.pendingAvatarFile = null;
    this.pendingAvatarPreviewUrl = null;
  }

  async createTeam() {
    const requestBody = {
      permalink: this.formFields.permalink.value,
      title: this.formFields.title.value,
    };
    const { team } = await createTeam(requestBody);
    if (team) {
      const createdTeam = new TeamModel(this.rootStore, team);
      this.rootStore.teams.loadTeams();
      this.rootStore.teams.setCurrentTeam(createdTeam);
      runInAction(() => {
        this.team = createdTeam;
      });
      const fileToUpload = this.pendingAvatarFile;
      if (fileToUpload) {
        await createdTeam.uploadAvatar({ avatar: { file: fileToUpload } });
      }
    }
  }

  open() {
    this.isOpen = true;
    this.resetFormFields();
  }

  close() {
    this.isOpen = false;
    this.resetFormFields();
  }

  setStep(step: "title" | "permalink" | "avatar" | "invites") {
    this.step = step;
  }

  resetFormFields() {
    this.setStep("title");
    this.team = null;
    this.clearPendingAvatar();
    this.formFields.permalink.value = "";
    this.formFields.permalink.error = null;
    this.formFields.title.value = "";
    this.formFields.title.error = null;
  }
}
